// src/neetjee_guru/components/academicPlanner/PlannerCreationWizard.js
import React, { Component } from "react";
import { Modal, Button, Form, Row, Col, ProgressBar, Badge } from "react-bootstrap";
import * as Cookies from "es-cookie";
import { GET_SECTION_NAMES } from "../../graphql/academicPlannerQueries";

// Stream options are typed/curated — not fetched from backend
const STREAM_OPTIONS = [
  "JEE Mains & EAPCET (Telangana)",
  "JEE Mains & EAPCET (Andhra Pradesh)",
  "JEE Advanced",
  "NEET",
  "Senior Inter (MPC)",
  "Senior Inter (BiPC)",
  "Junior Inter (MPC)",
  "Junior Inter (BiPC)"
];

// Fallback academic years if backend returns empty
const FALLBACK_YEARS = [
  { id: 1, academic_year: "2026 - 2027" },
  { id: 2, academic_year: "2027 - 2028" },
  { id: 3, academic_year: "2025 - 2026" }
];

class PlannerCreationWizard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentStep: 1,
      submitting: false,

      // Step 1: Master Meta & Scope
      plannerName: "",
      academicYearId: "",
      streamName: "JEE Mains & EAPCET (Telangana)",
      scopeType: "Institute", // "Institute" = ALL branches, "Branch" = specific campus
      selectedBranchId: "",   // live branch id from getBranches
      selectedBranchSections: [], // sections belonging to the selected branch
      sectionIds: "ALL",

      // Step 2: Phase Breakdown
      phase1Days: 30,
      phase2Days: 40,
      phase3Days: 20,

      // Step 3: Schedule Start
      startDate: new Date().toISOString().split("T")[0],
      generateSampleTopics: false,

      // Internal
      fetchingSections: false
    };
  }

  componentDidUpdate(prevProps) {
    // When wizard opens fresh, seed academicYearId from first live year
    if (!prevProps.show && this.props.show) {
      const years = this.getLiveYears();
      if (years.length > 0 && !this.state.academicYearId) {
        this.setState({ academicYearId: String(years[0].id) });
      }
      // Pre-seed branch from branchesList
      const branches = this.props.branchesList || [];
      if (branches.length > 0 && !this.state.selectedBranchId) {
        this.setState({
          selectedBranchId: String(branches[0].id),
          selectedBranchSections: branches[0].sections || []
        });
      }
    }
  }

  getLiveYears() {
    const { academicYearsList } = this.props;
    return academicYearsList && academicYearsList.length > 0 ? academicYearsList : FALLBACK_YEARS;
  }

  handleBranchChange = async (branchId) => {
    const { branchesList, apolloClient, institutionId } = this.props;

    // First try sections already embedded in branchesList
    const branch = (branchesList || []).find(b => String(b.id) === String(branchId));
    const embeddedSections = branch && branch.sections ? branch.sections : [];

    this.setState({ selectedBranchId: branchId, selectedBranchSections: embeddedSections, fetchingSections: true });

    // If no embedded sections, fetch via getSectionNames query
    if (embeddedSections.length === 0 && apolloClient) {
      try {
        const res = await apolloClient.query({
          query: GET_SECTION_NAMES,
          variables: { institution_id: institutionId || 1, branch_id: parseInt(branchId, 10) },
          fetchPolicy: "cache-first"
        });
        const fetched = (res.data && res.data.getSectionNames) || [];
        this.setState({ selectedBranchSections: fetched });
      } catch (e) {
        console.warn("Could not fetch sections for branch:", e.message);
      }
    }

    this.setState({ fetchingSections: false, sectionIds: "ALL" });
  };

  handleNextStep = () => {
    const { currentStep, plannerName, scopeType, selectedBranchId } = this.state;
    this.setState({ formError: "" });

    if (currentStep === 1) {
      if (!plannerName || !plannerName.trim()) {
        this.setState({ formError: "Planner Title is required." });
        return;
      }
      if (plannerName.trim().length < 3) {
        this.setState({ formError: "Planner Title must be at least 3 characters." });
        return;
      }
      if (scopeType === "Branch" && !selectedBranchId) {
        this.setState({ formError: "Please select a target Campus Branch." });
        return;
      }
    }

    if (currentStep < 2) {
      this.setState({ currentStep: currentStep + 1 });
    }
  };

  handlePrevStep = () => {
    const { currentStep } = this.state;
    if (currentStep > 1) {
      this.setState({ currentStep: currentStep - 1 });
    }
  };

  handleFinalSubmit = (e) => {
    e.preventDefault();
    const { onCreatePlanner, onHide } = this.props;
    const {
      plannerName, academicYearId, streamName, scopeType,
      selectedBranchId, sectionIds, startDate, generateSampleTopics
    } = this.state;

    const isInstitute = scopeType === "Institute";
    const branchIdsValue = isInstitute ? "ALL" : (selectedBranchId || "ALL");
    const sectionIdsValue = isInstitute ? "ALL" : (sectionIds || "ALL");

    const activeInstitutionId = parseInt(this.props.institutionId || Cookies.get("institutionid") || Cookies.get("institute_id") || Cookies.get("institution_id") || "1", 10);

    const payload = {
      institution_id: activeInstitutionId,
      academic_planner: plannerName.trim(),
      academic_year_id: parseInt(academicYearId, 10),
      stream_name: streamName,
      state_ids: "ALL",
      branch_ids: branchIdsValue,
      class_ids: "ALL",
      section_ids: sectionIdsValue,
      start_date: startDate,
      generate_sample_topics: generateSampleTopics
    };

    if (onCreatePlanner) {
      onCreatePlanner(payload);
    }
    // Reset wizard
    this.setState({ currentStep: 1, plannerName: "", submitting: false });
    onHide();
  };

  renderStepContent() {
    const {
      currentStep, plannerName, academicYearId, streamName, scopeType,
      selectedBranchId, selectedBranchSections, sectionIds,
      startDate, phase1Days, phase2Days, phase3Days, generateSampleTopics, fetchingSections
    } = this.state;

    const { branchesList } = this.props;
    const liveYears = this.getLiveYears();
    const branches = branchesList && branchesList.length > 0 ? branchesList : [];

    // Derive selected branch name for confirmation step
    const selectedBranch = branches.find(b => String(b.id) === String(selectedBranchId));
    const selectedBranchName = selectedBranch ? selectedBranch.branch_name : selectedBranchId;

    switch (currentStep) {

      // ── STEP 1: Identity & Scope ──────────────────────────────────────────────
      case 1:
        return (
          <div>
            <div className="d-flex align-items-center mb-3">
              <span className="badge badge-primary mr-2 px-2 py-1">Step 1</span>
              <h6 className="font-weight-bold text-dark m-0">Master Identity & Scope Assignment</h6>
            </div>
            {this.state.formError && (
              <div className="alert alert-danger p-2 small mb-3">
                <i className="fal fa-exclamation-circle mr-1" /> {this.state.formError}
              </div>
            )}
            <Row>
              {/* Planner Title */}
              <Col md={12} className="mb-3">
                <Form.Label className="small font-weight-bold">Planner Title <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  required
                  placeholder="e.g. Senior Inter MPC Academic Master Planner 2026-2027"
                  value={plannerName}
                  onChange={(e) => this.setState({ plannerName: e.target.value })}
                />
                <Form.Text className="text-muted">Give this planner a clear, descriptive name for easy identification.</Form.Text>
              </Col>

              {/* Stream — curated typed list */}
              <Col md={6} className="mb-3">
                <Form.Label className="small font-weight-bold">Target Stream <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="select"
                  size="sm"
                  value={streamName}
                  onChange={(e) => this.setState({ streamName: e.target.value })}
                >
                  {STREAM_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </Form.Control>
              </Col>

              {/* Academic Year — live from DB */}
              <Col md={6} className="mb-3">
                <Form.Label className="small font-weight-bold">Academic Year <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="select"
                  size="sm"
                  value={academicYearId}
                  onChange={(e) => this.setState({ academicYearId: e.target.value })}
                >
                  {liveYears.map(yr => (
                    <option key={yr.id} value={String(yr.id)}>{yr.academic_year}</option>
                  ))}
                </Form.Control>
                {this.props.academicYearsList && this.props.academicYearsList.length > 0 && (
                  <Form.Text className="text-success small"><i className="fal fa-check-circle mr-1" />Live from database</Form.Text>
                )}
              </Col>

              {/* Scope Assignment */}
              <Col md={12} className="mb-2">
                <Form.Label className="small font-weight-bold">Scope Level <span className="text-danger">*</span></Form.Label>
                <div className="btn-group btn-group-sm d-block">
                  <button
                    type="button"
                    className={`btn btn-sm mr-2 ${scopeType === "Institute" ? "btn-primary" : "btn-outline-secondary"}`}
                    onClick={() => this.setState({ scopeType: "Institute" })}
                  >
                    <i className="fal fa-university mr-1" /> Institute Master Level
                    <span className="d-block small font-weight-normal">Applies to ALL campus branches</span>
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${scopeType === "Branch" ? "btn-warning text-dark" : "btn-outline-secondary"}`}
                    onClick={() => this.setState({ scopeType: "Branch" })}
                  >
                    <i className="fal fa-building mr-1" /> Branch Specific Level
                    <span className="d-block small font-weight-normal">Only applies to selected campus</span>
                  </button>
                </div>
              </Col>

              {/* Branch Selector — only visible when scope = Branch, live from DB */}
              {scopeType === "Branch" && (
                <>
                  <Col md={6} className="mb-3 mt-2">
                    <Form.Label className="small font-weight-bold">
                      Select Campus Branch <span className="text-danger">*</span>
                      {branches.length > 0 && (
                        <Badge variant="success" className="ml-2" style={{ fontSize: "10px" }}>
                          {branches.length} campuses loaded
                        </Badge>
                      )}
                    </Form.Label>
                    {branches.length > 0 ? (
                      <Form.Control
                        as="select"
                        size="sm"
                        value={selectedBranchId}
                        onChange={(e) => this.handleBranchChange(e.target.value)}
                      >
                        {branches.map(b => (
                          <option key={b.id} value={String(b.id)}>{b.branch_name}</option>
                        ))}
                      </Form.Control>
                    ) : (
                      <Form.Control
                        type="text"
                        size="sm"
                        placeholder="Enter branch ID (e.g. 101)"
                        value={selectedBranchId}
                        onChange={(e) => this.setState({ selectedBranchId: e.target.value })}
                      />
                    )}
                    {branches.length > 0 && (
                      <Form.Text className="text-success small"><i className="fal fa-check-circle mr-1" />Live campus data from server</Form.Text>
                    )}
                  </Col>

                  {/* Section Selector — updates dynamically when branch changes */}
                  <Col md={6} className="mb-3 mt-2">
                    <Form.Label className="small font-weight-bold">
                      Section Scope {fetchingSections && <i className="fal fa-spinner fa-spin ml-1" />}
                    </Form.Label>
                    <Form.Control
                      as="select"
                      size="sm"
                      value={sectionIds}
                      onChange={(e) => this.setState({ sectionIds: e.target.value })}
                    >
                      <option value="ALL">All Sections</option>
                      {selectedBranchSections.map(sec => (
                        <option key={sec.id} value={String(sec.id)}>{sec.section_name}</option>
                      ))}
                    </Form.Control>
                    {selectedBranchSections.length > 0 && (
                      <Form.Text className="text-success small">
                        <i className="fal fa-check-circle mr-1" />{selectedBranchSections.length} sections for this campus
                      </Form.Text>
                    )}
                  </Col>
                </>
              )}
            </Row>
          </div>
        );

      // ── STEP 2: Start Date & Confirmation ──────────────────────────────────────
      case 2: {
        const selectedYear = liveYears.find(y => String(y.id) === String(academicYearId));
        return (
          <div>
            <div className="d-flex align-items-center mb-3">
              <span className="badge badge-success mr-2 px-2 py-1">Step 2</span>
              <h6 className="font-weight-bold text-dark m-0">Schedule Start Date & Final Confirmation</h6>
            </div>
            <Row className="mb-3">
              <Col md={6} className="mb-3">
                <Form.Label className="small font-weight-bold">Academic Start Date <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="date" size="sm"
                  value={startDate}
                  onChange={(e) => this.setState({ startDate: e.target.value })}
                />
                <Form.Text className="text-muted">Day 1 of the academic planner schedule</Form.Text>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Label className="small font-weight-bold">Initial Syllabus State</Form.Label>
                <div className="mt-2">
                  <Form.Check
                    type="checkbox"
                    id="sample-topics-chk"
                    label="Auto-generate placeholder rows for each subject"
                    checked={generateSampleTopics}
                    onChange={(e) => this.setState({ generateSampleTopics: e.target.checked })}
                    className="small font-weight-bold text-muted"
                  />
                  <Form.Text className="text-muted">Uncheck if you plan to seed syllabus via CSV upload.</Form.Text>
                </div>
              </Col>
            </Row>
            <div className="card border-0 bg-light rounded p-3 mb-3">
              <h6 className="font-weight-bold small text-muted text-uppercase mb-2">Planner Summary</h6>
              <table className="table table-sm table-borderless mb-0 small">
                <tbody>
                  <tr>
                    <td className="font-weight-bold text-muted" style={{ width: "40%" }}>Planner Title</td>
                    <td className="text-dark font-weight-bold">{plannerName}</td>
                  </tr>
                  <tr>
                    <td className="font-weight-bold text-muted">Target Stream</td>
                    <td className="text-dark">{streamName}</td>
                  </tr>
                  <tr>
                    <td className="font-weight-bold text-muted">Academic Year</td>
                    <td className="text-dark">{selectedYear ? selectedYear.academic_year : academicYearId}</td>
                  </tr>
                  <tr>
                    <td className="font-weight-bold text-muted">Scope Assignment</td>
                    <td>
                      {scopeType === "Institute"
                        ? <span className="badge badge-primary">Institute Master — All Campuses</span>
                        : <span className="badge badge-warning text-dark">Branch: {selectedBranchName}</span>
                      }
                    </td>
                  </tr>
                  <tr>
                    <td className="font-weight-bold text-muted">Academic Start Date</td>
                    <td className="text-dark font-weight-bold">{startDate}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="alert alert-info small m-0">
              <i className="fal fa-database mr-1" />
              This will create the master planner record + scope map in MySQL. You can immediately seed syllabus topics via Bulk CSV Upload.
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  }

  render() {
    const { show, onHide } = this.props;
    const { currentStep, submitting } = this.state;
    const progressPercent = Math.round((currentStep / 2) * 100);

    return (
      <Modal show={show} onHide={onHide} centered size="lg" className="planner-wizard-modal">
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title className="h6 font-weight-bold m-0 text-white">
            <i className="fal fa-magic mr-2" /> Academic Planner Setup Wizard
            <span className="ml-2 font-weight-normal" style={{ opacity: 0.8 }}>— Step {currentStep} of 2</span>
          </Modal.Title>
        </Modal.Header>

        <Form onSubmit={this.handleFinalSubmit}>
          <Modal.Body className="p-4">
            <ProgressBar
              now={progressPercent}
              label={`${progressPercent}%`}
              className="mb-4"
              style={{ height: "6px", borderRadius: "4px" }}
              animated={currentStep < 2}
            />
            {this.renderStepContent()}
          </Modal.Body>

          <Modal.Footer className="d-flex justify-content-between">
            <div>
              {currentStep > 1 && (
                <Button variant="outline-secondary" size="sm" onClick={this.handlePrevStep}>
                  <i className="fal fa-chevron-left mr-1" /> Back
                </Button>
              )}
            </div>

            <div className="d-flex align-items-center">
              <Button variant="light" size="sm" className="mr-2" onClick={onHide}>
                Cancel
              </Button>

              {currentStep < 2 ? (
                <Button variant="primary" size="sm" onClick={this.handleNextStep} className="font-weight-bold">
                  Continue <i className="fal fa-chevron-right ml-1" />
                </Button>
              ) : (
                <Button
                  variant="success"
                  size="sm"
                  type="submit"
                  className="font-weight-bold"
                  disabled={submitting}
                >
                  {submitting
                    ? <><i className="fal fa-spinner fa-spin mr-1" /> Creating...</>
                    : <><i className="fal fa-check mr-1" /> Create Master Planner</>
                  }
                </Button>
              )}
            </div>
          </Modal.Footer>
        </Form>
      </Modal>
    );
  }
}

export default PlannerCreationWizard;
